import { gql } from "@apollo/client";
import { ActionIcon, SegmentedControl, Stack, Tooltip } from "@mantine/core";
import { IconQrcode, IconTicket, IconUsers } from "@tabler/icons-react";
import { graphql } from "gql.tada";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Event } from "../../../../gql/graphql";
import { AutoDataTable } from "../../components/AutoDataTable";
import { ListPageHeader } from "../../components/PageHeader";
import { StatusBadge } from "../../components/StatusBadge";

const EVENTS_QUERY = graphql(`
  query events($cursor: String, $count: Float, $status: EventsQueryStatus) {
    events(
      query: {
        orderBy: REGISTRATIONS_START_AT
        orderDirection: DESC
        showNotVisible: true
        status: $status
      }
      page: { after: $cursor, first: $count }
    ) {
      nodes {
        id
        title
        startAt
        endAt
        startRegistrationsAt
        endRegistrationsAt
        visibleAt
      }
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

type EventStatusFilter =
  | "ALL"
  | "STARTED"
  | "ENDED"
  | "REGISTRATIONS_OPEN"
  | "REGISTRATIONS_ENDED";

const deriveEventStatus = (event: {
  startAt?: string | null;
  endAt?: string | null;
  startRegistrationsAt?: string | null;
  endRegistrationsAt?: string | null;
  visibleAt?: string | null;
}): string => {
  const now = new Date();
  if (event.endAt && new Date(event.endAt) <= now) return "Ended";
  if (event.startAt && new Date(event.startAt) <= now) return "In Progress";
  if (event.endRegistrationsAt && new Date(event.endRegistrationsAt) <= now)
    return "Registrations Closed";
  if (event.startRegistrationsAt && new Date(event.startRegistrationsAt) <= now)
    return "Registrations Open";
  if (event.visibleAt && new Date(event.visibleAt) <= now) return "Visible";
  return "Draft";
};

const formatRelativeDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "Not set";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (Math.abs(diffDays) < 1) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";
  if (diffDays > 0 && diffDays <= 7) return `In ${diffDays} days`;
  if (diffDays < 0 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`;
  return date.toLocaleDateString();
};

export const EventList = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<EventStatusFilter>("ALL");

  return (
    <div className="wp-fade-in">
      <ListPageHeader
        title="Events"
        createLabel="Create Event"
        onCreate={() => navigate("../new")}
        showBreadcrumbs={false}
      />
      <Stack gap="md">
        <SegmentedControl
          value={statusFilter}
          onChange={(value) => setStatusFilter(value as EventStatusFilter)}
          data={[
            { label: "All", value: "ALL" },
            { label: "Started", value: "STARTED" },
            { label: "Ended", value: "ENDED" },
            { label: "Registrations Open", value: "REGISTRATIONS_OPEN" },
            { label: "Registrations Ended", value: "REGISTRATIONS_ENDED" },
          ]}
        />
        <AutoDataTable
          itemPath={".."}
          createButtonText={false}
          deleteMutation={gql`
            mutation ($id: ID!) {
              eventDelete(id: $id)
            }
          `}
          query={EVENTS_QUERY}
          queryVariables={{
            status: statusFilter,
          }}
          columns={[
            {
              accessor: "title",
              title: "Title",
            },
            {
              accessor: "status",
              title: "Status",
              rawElement: (event: Event) => (
                <StatusBadge status={deriveEventStatus(event)} />
              ),
            },
            {
              accessor: "startAt",
              title: "Start date",
              description: "The date when the event starts",
              rawElement: (event: Event) => (
                <>{formatRelativeDate(event.startAt)}</>
              ),
            },
            {
              accessor: "startRegistrationsAt",
              title: "Registrations",
              description: "When registrations open",
              rawElement: (event: Event) => (
                <>{formatRelativeDate(event.startRegistrationsAt)}</>
              ),
            },
            {
              accessor: "actions",
              title: "",
              rawElement: (event) => (
                <>
                  <Tooltip label="Manage reservations">
                    <ActionIcon
                      onClick={() =>
                        navigate("../" + event.id + "/reservations")
                      }
                    >
                      <IconUsers />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Ticket configurations">
                    <ActionIcon
                      onClick={() =>
                        navigate("../" + event.id + "/settings/tickets")
                      }
                    >
                      <IconTicket />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="QR code scanner">
                    <ActionIcon
                      onClick={() => navigate("../" + event.id + "/qr-scanner")}
                    >
                      <IconQrcode />
                    </ActionIcon>
                  </Tooltip>
                </>
              ),
            },
          ]}
        />
      </Stack>
    </div>
  );
};
